import { useNavigate, useMatch, useLocation } from "react-router-dom";

import { useMobile } from "@krea/customer-features/hooks";
import { Text } from "@krea/customer-features/shared-components/text";
import { twMerge } from "@krea/shared/design-system/tailwind-utils";

interface IProgressBarItemProps {
  link?: string;
  step: { id: string; title: string };
  number: number;
  disabled?: boolean;
  last?: boolean;
  isAllowed?: boolean;
  isTitle?: boolean;
  count?: number;
  hideStepsMenu?: () => void;
}

const ProgressBarItem = ({
  link,
  step,
  number,
  disabled,
  last,
  isAllowed,
  isTitle,
  count,
  hideStepsMenu,
}: IProgressBarItemProps) => {
  const isMobile = useMobile();
  const location = useLocation();
  const match = useMatch(location.pathname);
  const navigate = useNavigate();

  const onClickItem = () => {
    if (link && !disabled && isAllowed) {
      navigate(link);
    }
  };

  const isPathMatching = () => {
    if (!match) {
      return false;
    }

    switch (step.id) {
      case "dashboard":
        return match.pathname.includes("dashboard");
      case "selected":
        return match.pathname.includes("selected");
      case "advice":
        return match.pathname.includes("advice");
      case "preview":
        return (
          match.pathname.includes("preview") ||
          match.pathname.includes("questions")
        );
      default:
        return false;
    }
  };

  return (
    <>
      {isTitle ? (
        <>
          {isPathMatching() && (
            <div
              className="tw-flex tw-grow tw-items-center tw-py-2 tw-font-bold xl:tw-py-0"
              style={{ minWidth: "0px" }}
            >
              <Text
                size="sm"
                className="tw-mr-2 tw-shrink-0 tw-rounded-none tw-bg-gray-disabled tw-px-2 tw-text-white"
              >{`${number}/${count}`}</Text>
              <Text
                size="sm"
                className="tw-truncate"
                style={{ minWidth: "0px" }}
              >
                {step.title}
              </Text>
            </div>
          )}
        </>
      ) : (
        <>
          <li
            aria-hidden
            className="tw-flex tw-items-center tw-py-2 xl:tw-py-0"
            style={{ cursor: !disabled && isAllowed ? "pointer" : "inherit" }}
            onClick={() => {
              onClickItem();
              if (hideStepsMenu) hideStepsMenu();
            }}
          >
            <Text
              size={isMobile ? "sm" : "md"}
              className={twMerge(
                "tw-w-8 tw-h-8 xl:tw-border-2 tw-flex tw-items-center tw-justify-center tw-font-bold tw-mr-2 tw-rounded-full tw-border tw-shrink-0",
                {
                  "tw-bg-black tw-border-black tw-text-white": isPathMatching(),
                  "tw-border-gray-disabled tw-text-gray-disabled":
                    !!disabled || !isAllowed,
                  "tw-border-black tw-text-black":
                    !isPathMatching() && !disabled && isAllowed,
                },
              )}
            >
              {number}
            </Text>
            <Text
              size={isMobile ? "sm" : "md"}
              className={twMerge("tw-font-bold tw-grow-[1] tw-truncate", {
                "tw-text-black": isPathMatching(),
                "tw-text-gray-disabled": !!disabled || !isAllowed,
              })}
              style={{ minWidth: "0px" }}
            >
              {step.title}
            </Text>
          </li>

          {!last && !isMobile && (
            <li className="tw-mx-3 tw-grow tw-border-t tw-border-gray-disabled" />
          )}
        </>
      )}
    </>
  );
};

export default ProgressBarItem;
